import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useReveal from '../../hooks/useReveal';
import { createLead } from '../../services/leadService';
import { getPromotions } from '../../services/promotionService';
import {
  BUSINESS, WHATSAPP, ROUTES, GOOGLE_PLACE_URL, GOOGLE_RATING,
  HERO_SLIDES, SERVICES, REVIEWS, STATS, FAQS, serviceUrl,
} from '../../data/pmContent';
import {
  IconArrow, IconCheck, IconClock, IconShield, IconCalendar, IconHeart,
  IconStar, IconBadgeCheck, IconInfo, IconSearch, IconList, IconPin,
  IconParking, IconPlay, IconChevronDown, IconWhatsapp,
} from './PmIcons';

/* ========================================================================= */
/*  HERO SLIDER                                                              */
/* ========================================================================= */
function HeroSlider() {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduced = useRef(false);

  useEffect(() => {
    reduced.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  useEffect(() => {
    if (paused || reduced.current) return undefined;
    const t = setInterval(() => setCurrent((c) => (c + 1) % HERO_SLIDES.length), 5200);
    return () => clearInterval(t);
  }, [paused]);

  useEffect(() => {
    const onVis = () => setPaused(document.hidden);
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  return (
    <div className="hero__media reveal reveal-scale" data-delay="200">
      <div
        className="hero__slides"
        role="region"
        aria-label="Galería de servicios"
        aria-roledescription="carrusel"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
      >
        {HERO_SLIDES.map((s, i) => (
          <figure
            key={s.src}
            className={`hero__slide${i === current ? ' is-active' : ''}`}
            aria-hidden={i !== current}
          >
            <img
              src={s.src}
              alt={s.alt}
              width={s.w}
              height={s.h}
              loading={i === 0 ? 'eager' : 'lazy'}
              fetchpriority={i === 0 ? 'high' : undefined}
              decoding="async"
            />
            <figcaption>{s.caption}</figcaption>
          </figure>
        ))}
      </div>

      <div className="hero__dots" role="tablist" aria-label="Cambiar imagen">
        {HERO_SLIDES.map((s, i) => (
          <button
            key={s.src}
            type="button"
            role="tab"
            aria-selected={i === current}
            aria-label={`Ver imagen ${i + 1} de ${HERO_SLIDES.length}`}
            onClick={() => setCurrent(i)}
          />
        ))}
      </div>

      {/* Tarjeta de rating: solo se muestra cuando haya datos reales de Google */}
      <div className="hero__float" aria-hidden="true">
        <div>
          <span className="stars">★★★★★</span>
          <strong>{GOOGLE_RATING.value} en Google</strong>
          <span>{GOOGLE_RATING.count} reseñas de clientas</span>
        </div>
      </div>
    </div>
  );
}

/* ========================================================================= */
/*  FACHADA DE VIDEO (no carga el iframe de YouTube hasta el clic)           */
/* ========================================================================= */
function VideoFacade() {
  const [playing, setPlaying] = useState(false);

  if (playing) {
    return (
      <div className="video-facade" style={{ cursor: 'default' }}>
        <iframe
          src={`https://www.youtube.com/embed/${BUSINESS.youtubeId}?autoplay=1&rel=0`}
          title={`Recorrido por ${BUSINESS.name}`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture"
          allowFullScreen
          style={{ border: 0 }}
        />
      </div>
    );
  }

  return (
    <button
      className="video-facade"
      type="button"
      onClick={() => setPlaying(true)}
      aria-label="Reproducir el recorrido en video por el estudio"
    >
      <img src="/assets/paomakeup/images/services/salon-belleza.jpg" alt="" width="1082" height="1080" loading="lazy" decoding="async" />
      <span className="video-facade__play"><span><IconPlay /></span></span>
    </button>
  );
}

/* ========================================================================= */
/*  FAQ                                                                      */
/* ========================================================================= */
function Faq() {
  const [open, setOpen] = useState(-1);
  return (
    <div className="faq" data-stagger="50">
      {FAQS.map((f, i) => (
        <details
          key={f.q}
          className="reveal reveal-up"
          open={open === i}
          onToggle={(e) => { if (e.target.open) setOpen(i); else if (open === i) setOpen(-1); }}
        >
          <summary>
            {f.q}
            <span className="chev"><IconChevronDown /></span>
          </summary>
          <div className="faq__answer">
            <p>{f.a}{f.pending && <em> (Dato por confirmar con el estudio.)</em>}</p>
          </div>
        </details>
      ))}
    </div>
  );
}

/* ========================================================================= */
/*  FORMULARIO DE EVALUACIÓN                                                 */
/* ========================================================================= */
function LeadForm() {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  /* Crea el lead en AgendaMax (source: home_hero) y lleva a la página de
     evaluación con nombre/teléfono precargados, para no pedir los datos dos veces.
     Si el lead falla (ej. duplicado), igual redirige: el formulario completo lo reintenta. */
  const onSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const name = form.name.value.trim();
    const phone = form.phone.value.trim();
    const interest = form.interest.value;
    if (!name || !phone) { form.reportValidity(); return; }

    setSending(true);
    try {
      await createLead({ name, phone, source: 'home_hero' });
    } catch (err) {
      console.warn('Lead creation failed:', err);
    }
    setSending(false);
    setSent(true);
    navigate(ROUTES.evaluacion, { state: { name, phone, interest } });
  };

  return (
    <form className="lead-form" onSubmit={onSubmit}>
      <h3>Solicita tu evaluación</h3>
      <div className="field">
        <label htmlFor="lf-name">Nombre</label>
        <input id="lf-name" name="name" type="text" autoComplete="name" required minLength="3" placeholder="Tu nombre" />
      </div>
      <div className="field">
        <label htmlFor="lf-phone">WhatsApp</label>
        <input id="lf-phone" name="phone" type="tel" autoComplete="tel" required placeholder="809-000-0000" />
      </div>
      <div className="field">
        <label htmlFor="lf-interest">¿Qué te interesa?</label>
        <select id="lf-interest" name="interest" defaultValue="Microblading / cejas">
          <option>Microblading / cejas</option>
          <option>Delineado de ojos</option>
          <option>Labios</option>
          <option>Todavía no lo sé</option>
        </select>
      </div>
      <button className="btn btn--primary btn--block" type="submit" disabled={sending || sent}>
        {sending ? 'Enviando…' : 'Solicitar evaluación gratis'}
      </button>
      <small>Solo dos datos. Te contactamos por WhatsApp para coordinar. No compartimos tu información.</small>
      <div className={`form-success${sent ? ' is-visible' : ''}`} role="status">
        <IconCheck width="20" height="20" /> ¡Listo! Completa la evaluación para agilizar tu cita.
      </div>
    </form>
  );
}

/* ========================================================================= */
/*  OFERTAS (leídas del backend)                                             */
/* ========================================================================= */
const offerExpired = (expiresAt) => expiresAt && new Date(expiresAt) < new Date();

/* Solo el arte del flyer, con el CTA de reserva encima. Todo el contenido
   (combos, precios, condiciones) vive en la imagen que sube el negocio. */
function OfferCard({ promo }) {
  return (
    <article className="offer offer--flyer reveal reveal-up">
      <div className="offer__cta">
        <Link className="btn btn--primary" to={ROUTES.agendar}>
          Reservar mi cita <IconArrow />
        </Link>
      </div>
      <div className="offer__media">
        <img src={promo.image_url} alt={promo.title} width="1080" height="1920" loading="lazy" decoding="async" />
      </div>
    </article>
  );
}

/* ========================================================================= */
/*  HOME                                                                     */
/* ========================================================================= */
export default function PmHome() {
  /* Promociones vigentes desde AgendaMax. null = cargando; [] = sin promos
     (o el API falló): la sección no se muestra para no enseñar ofertas viejas. */
  const [offers, setOffers] = useState(null);
  useEffect(() => {
    let alive = true;
    getPromotions()
      .then((res) => {
        if (!alive) return;
        const list = (res.data?.data ?? res.data ?? []).filter(
          (p) => p.is_active !== false && p.image_url && !offerExpired(p.expires_at)
        );
        setOffers(list);
      })
      .catch(() => { if (alive) setOffers([]); });
    return () => { alive = false; };
  }, []);

  // Re-observa los elementos .reveal cuando las ofertas llegan del API
  useReveal([offers]);

  return (
    <main id="main">

      {/* ================= HERO ================= */}
      <section className="hero">
        <div className="container container--wide hero__grid">
          <div className="hero__copy">
            <span className="hero__badge reveal reveal-up">
              <span className="dot" /> Estudio de belleza en Los Prados, Santo Domingo
            </span>

            <h1 className="reveal reveal-up text-balance" data-delay="90">
              Sal lista para brillar, <em>sin retoques cada mañana</em>
            </h1>

            <p className="hero__sub reveal reveal-up" data-delay="180">
              Maquillaje semipermanente, cejas, pestañas, piel, uñas y peluquería en un solo estudio.
              Un equipo que diseña tu look según tus facciones — no según una plantilla.
            </p>

            <div className="hero__cta reveal reveal-up" data-delay="260">
              <Link className="btn btn--primary btn--lg" to={ROUTES.agendar}>
                Agendar mi cita <IconArrow />
              </Link>
              <a className="btn btn--secondary btn--lg" href="#evaluacion">Evaluación gratis de cejas</a>
            </div>

            <p className="hero__note reveal reveal-fade" data-delay="340">
              Reserva online en menos de 2 minutos · Sin pago por adelantado
            </p>

            <div className="hero__trust reveal reveal-up" data-delay="400">
              <div><strong>7 categorías</strong><span>Todo en un mismo lugar</span></div>
              <div><strong>Online 24/7</strong><span>Agenda cuando quieras</span></div>
              <div><strong>Los Prados</strong><span>Juan Isidro Ortega #302</span></div>
            </div>
          </div>

          <HeroSlider />
        </div>
      </section>

      {/* ================= GARANTÍAS ================= */}
      <section className="assurance" aria-label="Por qué elegirnos">
        <div className="container container--wide assurance__grid" data-stagger="60">
          <div className="assurance__item reveal reveal-up">
            <IconShield />
            <div><strong>Diseño personalizado</strong><span>Según tus facciones, no plantillas</span></div>
          </div>
          <div className="assurance__item reveal reveal-up">
            <IconCalendar />
            <div><strong>Reserva online</strong><span>Elige día, hora y servicio</span></div>
          </div>
          <div className="assurance__item reveal reveal-up">
            <IconHeart />
            <div><strong>Todo en un lugar</strong><span>Cejas, piel, uñas y cabello</span></div>
          </div>
          <div className="assurance__item reveal reveal-up">
            <IconClock />
            <div><strong>Parqueo disponible</strong><span>{BUSINESS.hoursShort}</span></div>
          </div>
        </div>
      </section>

      {/* ================= OFERTAS (desde el backend) ================= */}
      {offers && offers.length > 0 && (
        <section className="section section--blush" id="ofertas">
          <div className="container">
            <div className="section-head reveal reveal-up">
              <span className="eyebrow">Promociones vigentes</span>
              <h2 className="text-balance">Ofertas del mes</h2>
              <p>Precios especiales por tiempo limitado. Reserva directamente desde aquí — no necesitas llamar.</p>
            </div>

            <div className="offers__grid" data-stagger="80">
              {offers.map((promo) => (
                <OfferCard promo={promo} key={promo.id} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ================= SERVICIOS ================= */}
      <section className="section" id="servicios">
        <div className="container">
          <div className="section-head section-head--center reveal reveal-up">
            <span className="eyebrow eyebrow--center">Nuestros servicios</span>
            <h2 className="text-balance">Todo lo que necesitas para verte y sentirte bien</h2>
            <p>Siete áreas de servicio bajo el mismo techo, con el mismo estándar de acabado.</p>
          </div>

          <div className="services__grid" data-stagger="70">
            {SERVICES.map((s) => (
              <article className="service reveal reveal-up" key={s.slug}>
                <div className="service__media">
                  {s.badge && <span className="service__badge">{s.badge}</span>}
                  {s.image ? (
                    <img src={s.image} alt={s.imageAlt} width={s.w} height={s.h} loading="lazy" decoding="async" />
                  ) : (
                    <div className="service__placeholder" aria-hidden="true">
                      <IconStar width="52" height="52" />
                    </div>
                  )}
                </div>
                <div className="service__body">
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                  <div className="service__facts">
                    {s.priceFrom && <span className="service__fact">{s.priceFrom}</span>}
                    <span className="service__fact">{s.fact}</span>
                  </div>
                  <div className="service__actions">
                    <Link className="btn btn--secondary btn--sm" to={serviceUrl(s.slug)}>Ver precios</Link>
                    <Link className="btn btn--primary btn--sm" to={ROUTES.agendar}>Agendar</Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ================= PROCESO ================= */}
      <section className="section section--surface">
        <div className="container">
          <div className="section-head section-head--center reveal reveal-up">
            <span className="eyebrow eyebrow--center">Cómo funciona</span>
            <h2 className="text-balance">Tres pasos y estás lista</h2>
          </div>
          <div className="steps" data-stagger="80">
            <div className="step reveal reveal-up">
              <h3>Elige tu servicio</h3>
              <p>Explora las categorías y revisa precios sin tener que llamar. Si no sabes cuál te conviene, escríbenos por WhatsApp.</p>
            </div>
            <div className="step reveal reveal-up">
              <h3>Reserva tu horario</h3>
              <p>Agenda online, eliges día y hora. Sin pago por adelantado y con confirmación inmediata.</p>
            </div>
            <div className="step reveal reveal-up">
              <h3>Sal lista para brillar</h3>
              <p>Llegas, te atendemos a la hora reservada y sales con un acabado hecho a tu medida.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= EVALUACIÓN GRATIS ================= */}
      <section className="section" id="evaluacion">
        <div className="container">
          <div className="lead-band reveal reveal-scale">
            <div className="lead-band__grid">
              <div>
                <span className="eyebrow">Sin costo · Sin compromiso</span>
                <h2 className="text-balance">Evaluación gratis para maquillaje semipermanente</h2>
                <p>Antes de decidirte, queremos ver tus cejas. Evaluamos tu tipo de piel, la forma de tu rostro y te decimos con honestidad si el procedimiento es para ti.</p>
                <ul>
                  <li><IconCheck strokeWidth="2" /> Diagnóstico de tu tipo de piel y forma de ceja</li>
                  <li><IconCheck strokeWidth="2" /> Simulación del diseño antes de cualquier procedimiento</li>
                  <li><IconCheck strokeWidth="2" /> Presupuesto claro, sin sorpresas</li>
                  <li><IconCheck strokeWidth="2" /> Si no eres candidata, te lo decimos</li>
                </ul>
              </div>
              <LeadForm />
            </div>
          </div>
        </div>
      </section>

      {/* ================= MEMBRESÍAS ================= */}
      <section className="section section--blush" id="membresias">
        <div className="container">
          <div className="section-head section-head--center reveal reveal-up">
            <span className="eyebrow eyebrow--center">Para clientas frecuentes</span>
            <h2 className="text-balance">Membresías y programa de lealtad</h2>
            <p>Si vienes cada mes, no tiene sentido pagar como si fuera tu primera vez.</p>
          </div>

          <div className="member__grid" data-stagger="80">
            <div className="member reveal reveal-up">
              <div className="member__icon"><IconHeart /></div>
              <h3>Ahorro real</h3>
              <p>Tarifa preferencial en tus servicios recurrentes.</p>
              <ul>
                <li><IconCheck strokeWidth="2.4" /> Precio de miembro todo el año</li>
                <li><IconCheck strokeWidth="2.4" /> Beneficios en servicios combinados</li>
              </ul>
            </div>

            <div className="member member--featured reveal reveal-up">
              <div className="member__icon"><IconStar /></div>
              <h3>Atención preferencial</h3>
              <p>Prioridad de horarios y agenda reservada para miembros.</p>
              <ul>
                <li><IconCheck strokeWidth="2.4" /> Cupos reservados en fechas pico</li>
                <li><IconCheck strokeWidth="2.4" /> Recordatorio de retoque a tiempo</li>
              </ul>
              <Link className="btn btn--primary" to={serviceUrl('membresias')}>Ver planes y precios</Link>
            </div>

            <div className="member reveal reveal-up">
              <div className="member__icon"><IconBadgeCheck /></div>
              <h3>Sellos de lealtad</h3>
              <p>Acumula sellos con cada visita y canjéalos por servicios.</p>
              <ul>
                <li><IconCheck strokeWidth="2.4" /> Se acumulan en tu cuenta automáticamente</li>
                <li><IconCheck strokeWidth="2.4" /> Consulta tu saldo al iniciar sesión</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ================= RESEÑAS ================= */}
      <section className="section" id="resenas">
        <div className="container">
          <div className="section-head section-head--center reveal reveal-up">
            <span className="eyebrow eyebrow--center">Lo que dicen las clientas</span>
            <h2 className="text-balance">Reseñas reales, no promesas</h2>
          </div>

          {REVIEWS.length === 0 && (
            <div className="proof-note reveal reveal-fade">
              <IconInfo />
              <div>
                <strong>Sección construida, contenido pendiente.</strong>{' '}
                Llenar <code>REVIEWS</code> en <code>src/data/pmContent.js</code> con reseñas reales
                de Google Business Profile.
              </div>
            </div>
          )}

          <div className="reviews" data-stagger="70">
            {REVIEWS.map((r) => (
              <article className="review reveal reveal-up" key={r.name + r.text.slice(0, 20)}>
                <div className="review__stars" aria-label={`${r.rating} de 5 estrellas`}>
                  {'★'.repeat(r.rating)}
                </div>
                <blockquote>{`“${r.text}”`}</blockquote>
                <div className="review__author">
                  <div className="review__avatar" aria-hidden="true">{r.name.charAt(0).toUpperCase()}</div>
                  <div>
                    <strong>{r.name}</strong>
                    {r.service && <span>{r.service}</span>}
                  </div>
                </div>
              </article>
            ))}
          </div>

          {REVIEWS.length > 0 && (
            <p className="reviews__source reveal reveal-fade">
              Reseñas publicadas por clientas en Google. Texto sin editar.
              <a className="link-arrow" href={GOOGLE_PLACE_URL} target="_blank" rel="noopener noreferrer">
                <span>Ver todas en Google</span>
                <IconArrow width="16" height="16" />
              </a>
            </p>
          )}

          <div className="stats" data-stagger="60">
            {STATS.map((s) => (
              <div className="stat reveal reveal-up" key={s.label}>
                <strong>
                  {s.value || '—'}
                  {s.star && <span aria-hidden="true" style={{ color: 'var(--pm-primary)' }}>★</span>}
                </strong>
                <span>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= INSTALACIONES ================= */}
      <section className="section section--surface">
        <div className="container venue__grid">
          <div className="reveal reveal-left"><VideoFacade /></div>

          <div className="reveal reveal-right">
            <span className="eyebrow">Nuestro espacio</span>
            <h2 className="text-balance">Visita nuestras instalaciones</h2>
            <p className="lead" style={{ marginTop: 'var(--space-4)' }}>
              Un estudio pensado para que la visita sea tan buena como el resultado.
            </p>
            <ul className="venue__list">
              <li><IconPin /> {BUSINESS.address}</li>
              <li><IconClock /> {BUSINESS.hours}</li>
              <li><IconParking /> {BUSINESS.parking}</li>
            </ul>
            <p style={{ marginTop: 'var(--space-6)' }}>
              <a className="link-arrow" href={BUSINESS.mapsUrl} target="_blank" rel="noopener noreferrer">
                <span>Cómo llegar en Google Maps</span>
                <IconArrow width="16" height="16" />
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* ================= FAQ ================= */}
      <section className="section" id="faq">
        <div className="container">
          <div className="section-head section-head--center reveal reveal-up">
            <span className="eyebrow eyebrow--center">Antes de agendar</span>
            <h2 className="text-balance">Preguntas frecuentes</h2>
            <p>Las dudas que hoy el sitio deja sin responder — y que frenan la reserva.</p>
          </div>
          <Faq />
        </div>
      </section>

      {/* ================= CTA FINAL ================= */}
      <section className="section section--lg final-cta">
        <div className="container final-cta__inner">
          <span className="eyebrow eyebrow--center reveal reveal-fade">El siguiente paso</span>
          <h2 className="text-balance reveal reveal-up" data-delay="80">¿Lista para empezar a brillar?</h2>
          <p className="reveal reveal-up" data-delay="150">
            Elige tu servicio, reserva tu horario y déjanos el resto. Sin pago por adelantado.
          </p>

          <div className="final-cta__buttons reveal reveal-up" data-delay="220">
            <Link className="btn btn--light btn--lg" to={ROUTES.agendar}>
              Agendar mi cita <IconArrow />
            </Link>
          </div>

          <div className="final-cta__alt reveal reveal-fade" data-delay="300">
            <Link to={ROUTES.cotizar}><IconList /> Cotizar un evento</Link>
            <a href="#evaluacion"><IconSearch /> Evaluación gratis de cejas</a>
            <a href={WHATSAPP} target="_blank" rel="noopener noreferrer"><IconWhatsapp width="16" height="16" /> Escribir por WhatsApp</a>
          </div>
        </div>
      </section>

    </main>
  );
}
